import * as React from "react"
import {profiler} from "../profiler"
import {current_user, ui_actions} from "../state/index"
import {full_name, not_null} from "@cling/lib.shared.utils"
import {with_perfect_scrollbar} from "@cling/lib.web.utils"
import type {RouteComponentProps} from "@reach/router"
import {TopAppBar} from "@cling/lib.web.mdc/top_app_bar"
import {Page} from "@cling/lib.web.mdc/page"
import {TypographyLogo} from "../board/toolbar_items_common"
import {Card} from "@cling/lib.web.mdc/card"
import {Button} from "@cling/lib.web.mdc/button"
import {LoadingIndicator} from "@cling/lib.web.lazy_load/loading_indicator"
import {call_function} from "@cling/lib.shared.faas/index"
import {
    APIScope,
    CLING_BUTTON_OAUTH2_CLIENT_ID,
    InitOauth2FlowAuthorize,
    InitOauth2FlowRequest,
    InitOauth2FlowResponse,
    InitOauth2FlowValidate,
} from "@cling/lib.shared.model/model"
import {report_error} from "@cling/lib.shared.debug/index"
import {i18n} from "@cling/lib.web.i18n/index"
import {query_param} from "@cling/lib.web.utils/query_param"

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const AuthorizeAppPage = (_: RouteComponentProps) => {
    React.useEffect(() => {
        NProgress.done()
        profiler.on_page_mounted()
        ui_actions.toggle_desktop_board_chooser("hidden")
    }, [])
    const [waiting, set_waiting] = React.useState(true)
    const [error_msg, set_error_msg] = React.useState<string>()
    const [client_name, set_client_name] = React.useState("")
    const [api_scopes, set_api_scopes] = React.useState<APIScope[]>([])
    const [decline_redirect_uri, set_decline_redirect_uri] = React.useState<string>()
    const handle_error = React.useCallback((error: any) => {
        report_error("Failed to validate OAuth2 request - showing an error", error)
        set_error_msg(i18n.failed_to_authorize_an_external_app)
        set_waiting(false)
    }, [])
    const allow = React.useCallback(() => {
        set_waiting(true)
        call_function(
            new InitOauth2FlowRequest({
                query_string: location.search.slice(1),
                authorize: new InitOauth2FlowAuthorize({}),
            }),
            InitOauth2FlowResponse,
        )
            .then(({authorize_response, error_redirect_uri}) => {
                if (error_redirect_uri) {
                    goto(error_redirect_uri)
                    return
                }
                const res = not_null(authorize_response, "`authorize_response` cannot be null")
                goto(res.redirect_uri)
            })
            .catch(handle_error)
    }, [handle_error])
    React.useEffect(() => {
        call_function(
            new InitOauth2FlowRequest({
                query_string: location.search.slice(1),
                validate: new InitOauth2FlowValidate({}),
            }),
            InitOauth2FlowResponse,
        )
            .then(({validate_response, error_redirect_uri}) => {
                if (error_redirect_uri) {
                    // If there is a `redirect_uri` in the response, we know that the request
                    // is invalid and we tell the calling client about it.
                    goto(error_redirect_uri)
                    return
                }
                const res = not_null(validate_response, "`validate_response` cannot be null")
                if (
                    query_param("client_id") === CLING_BUTTON_OAUTH2_CLIENT_ID &&
                    query_param("redirect_uri")?.startsWith(
                        `https://${location.host}/cling-button-auth`,
                    )
                ) {
                    allow()
                    return
                }
                set_client_name(res.client_name)
                set_api_scopes(res.api_scopes)
                set_decline_redirect_uri(res.decline_redirect_uri)
                set_waiting(false)
            })
            .catch(handle_error)
    }, [handle_error, allow])
    const decline = React.useCallback(() => {
        set_waiting(true)
        goto(decline_redirect_uri ?? "/c")
    }, [decline_redirect_uri])
    const title = i18n.authorize_app(client_name)
    const top_app_bar = (
        <TopAppBar
            more_items={
                <div className="mdc-top-app-bar__title toolbar-items__title toolbar-items__title--left flex-row align-items--center">
                    <TypographyLogo in_app />
                    <div>
                        {"//"} {title}
                    </div>
                </div>
            }
            className="public-page__top-app-bar"
        />
    )
    if (error_msg) {
        return (
            <Page document_title={title} top_app_bar={top_app_bar}>
                <div className="flex-row mt--2 p--2 justify-content--center">
                    <Card
                        title="Authorization failed"
                        className="p--2 overflow--hidden"
                        style={{maxWidth: "500px"}}
                    >
                        {error_msg}
                    </Card>
                </div>
                ,
            </Page>
        )
    }
    if (!client_name) {
        return (
            <Page document_title={title} top_app_bar={top_app_bar}>
                <div className="flex-row mt--2 p--2 justify-content--center">
                    <Card className="p--2 overflow--hidden" style={{maxWidth: "500px"}}>
                        <LoadingIndicator />
                    </Card>
                </div>
                ,
            </Page>
        )
    }
    return (
        <Page document_title={title} top_app_bar={top_app_bar}>
            {with_perfect_scrollbar(
                <div className="flex-row mt--2 p--2 justify-content--center">
                    <Card className="p--2 overflow--hidden" style={{maxWidth: "500px"}}>
                        {i18n.hi(full_name(current_user.account))}
                        <br />
                        <br />
                        <span>
                            {i18n.app_is_requesting_acces_to_the_following_data(
                                client_name,
                                current_user.account_attributes.email,
                            )}
                        </span>
                        <ul>
                            {api_scopes.map((x) => (
                                <li key={x}>{i18n.api_scope(x)}</li>
                            ))}
                        </ul>
                        <div className="flex-row flex-wrap--wrap justify-content--center">
                            <Button
                                disabled={waiting}
                                outlined
                                className="ml--2 mr--2 mt--2"
                                onClick={allow}
                                data-test-id="AuthorizeAppPage_allow"
                            >
                                <div className="flex-row align-items--center">
                                    {i18n.allow}
                                    {waiting && <LoadingIndicator />}
                                </div>
                            </Button>
                            <Button
                                onClick={decline}
                                disabled={waiting}
                                outlined
                                danger
                                className="mt--2"
                            >
                                {i18n.decline}
                            </Button>
                        </div>
                    </Card>
                </div>,
            )}
        </Page>
    )
}

export default AuthorizeAppPage
